import React, { useState } from 'react'
import { Grid, List } from 'semantic-ui-react'
import gql from 'graphql-tag'
import { useMutation } from 'react-apollo'
import { useHistory, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'

import { Form, InputField, SubmitButton, validate } from '@labsavvyapp/ui-components'

import style from './RecoverPassword.module.css'
import { SIGN } from '../../../config/routes'
import { Button, LabsavvyIQLogo, PasswordInput, useForm } from '@labsavvyapp/components'

const RECOVER_EMAIL = gql`
  mutation recoverEmail($proof: String!, $email: EmailAddress!, $newPassword: String!) {
    recoverEmail(proof: $proof, email: $email, newPassword: $newPassword)
  }
`

/**
 * Recover password. Step 3: Set new password.
 */
const RecoverNewPassword = () => {
  const { formatMessage } = useIntl()
  const { push } = useHistory()

  const [recoverEmail] = useMutation(RECOVER_EMAIL)
  const { search } = useLocation()
  const [formErrors, setFormErrors] = useState({})
  const INITIAL_FORM_DATA = {
    newPassword: '',
    newPasswordConfirmation: '',
  }

  /**
   * Checks if the strings are equal.
   */
  const isEqual = (newPassword, newPasswordConfirmation) => {
    return newPassword === newPasswordConfirmation
  }

  /**
   * Form submission.
   */
  // FIXME: Fix how server errors are handled (https://labsavvy.atlassian.net/browse/LS-511)
  const onSubmit = async ({ newPassword, newPasswordConfirmation }) => {
    const queryParams = new URLSearchParams(search)
    const email = queryParams.get('email')
    const proof = queryParams.get('proof')

    if (!isEqual(newPassword, newPasswordConfirmation)) {
      const serverError = new Error(
        JSON.stringify({
          id: 'server_error',
          defaultMessage: "Passwords doesn't match",
        }),
      )
      setFormErrors({
        newPasswordConfirmation: serverError,
      })
      return
    }

    try {
      await recoverEmail({
        variables: {
          proof,
          email,
          newPassword,
        },
      })

      push(SIGN.recoverFinish)
    } catch (error) {
      const serverError = new Error(
        JSON.stringify({
          id: 'server_error',
          defaultMessage: error?.graphQLErrors[0]?.message,
        }),
      )
      setFormErrors({
        newPasswordConfirmation: serverError,
      })
    }
  }

  const form = useForm({
    initialValues: {
      newPassword: '',
      newPasswordConfirmation: '',
    },
    validate: {
      newPassword: (value) => {
        if (!value || value.length < 1) {
          return 'Cannot be empty'
        }

        // Check length (8-12 characters)
        if (value.length < 8 || value.length > 12) {
          return 'Password must be 8-12 characters long'
        }

        // Check for at least 1 uppercase letter
        if (!/[A-Z]/.test(value)) {
          return 'Password must contain at least 1 uppercase letter'
        }

        // Check for at least 1 lowercase letter
        if (!/[a-z]/.test(value)) {
          return 'Password must contain at least 1 lowercase letter'
        }

        // Check for at least 1 number
        if (!/\d/.test(value)) {
          return 'Password must contain at least 1 number'
        }

        // Check for at least 1 special character
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
          return 'Password must contain at least 1 special character'
        }

        return null
      },
      newPasswordConfirmation: (value, values) => {
        if (!value || value.length < 1) {
          return 'Cannot be empty'
        }

        if (value !== values.newPassword) {
          return 'Passwords do not match'
        }

        return null
      },
    },
    validateInputOnChange: true,
  })

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(values))} className={style.changePasswordStep}>
      <Grid>
        <Grid.Column>
          <div className={style.head}>
            <LabsavvyIQLogo />
          </div>
          <Grid.Row>
            <Grid.Column>
              <div
                style={{
                  textAlign: 'left',
                  fontWeight: 200,
                }}
              >
                <p>Please enter the new password</p>
                <ul>
                  <li>8 to 12 characters in length</li>
                  <li>at least 1 Capital letter</li>
                  <li>at least 1 Lowercase letter</li>
                  <li>at least 1 Number, and</li>
                  <li>at least 1 Special character</li>
                </ul>
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <PasswordInput name="newPassword" placeholder="Choose a password" {...form.getInputProps('newPassword')} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <PasswordInput
                name="newPasswordConfirmation"
                placeholder="Enter password again"
                {...form.getInputProps('newPasswordConfirmation')}
                mt={15}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <div
                style={{
                  textAlign: 'left',
                  fontWeight: 200,
                  width: 190,
                  marginTop: 20,
                }}
              >
                <Button radius="xl" size="md" uppercase variant="filled" type="submit" fullWidth disabled={!form?.isValid()}>
                  Change Password
                </Button>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid.Column>
      </Grid>
    </form>
  )
}

export default RecoverNewPassword
